import Modal from "react-modal";
import { Tooltip } from "react-tooltip";
import { IModalProps } from "../../properties/ModalProperties";
import i18n from "../../language/i18n";

// Glass modal shell ("Playful Frost"). react-modal is kept for the portal + focus
// trap + ESC handling + body-scroll lock; only the chrome is restyled to the glass
// design: a frosted, blurred backdrop and a popover-glass card that pops in. The
// public API (title / isOpen / size / onAfterOpen / onRequestClose / children) is
// unchanged, so every modal across the app inherits the new look for free.
// Prefix: bm-.
const BaseModal = ({
  title,
  isOpen,
  size,
  icon,
  onAfterOpen,
  onRequestClose,
  hideCloseButton,
  truncateTitle,
  children,
}: IModalProps) => {
  const widthClass =
    size === "small" ? "bm-w-sm" : size === "medium" ? "bm-w-md" : "bm-w-lg";
  return (
    <Modal
      isOpen={isOpen}
      onAfterOpen={onAfterOpen}
      onRequestClose={onRequestClose}
      overlayClassName="bm-overlay"
      style={{
        content: {
          top: "50%",
          left: "50%",
          right: "auto",
          bottom: "auto",
          marginRight: "-50%",
          transform: "translate(-50%, -50%)",
          padding: 0,
          border: "none",
          background: "transparent",
          borderRadius: 0,
          overflow: "visible",
          outline: "none",
          zIndex: 20,
        },
        overlay: {
          // react-modal only applies its default overlay positioning when no
          // overlayClassName is given (it sets overlayStyles = {} otherwise), so
          // passing `overlayClassName="bm-overlay"` drops `position: fixed; inset: 0`.
          // Re-supply it here, else the overlay flows statically at the bottom of
          // <body> and the centered card anchors to the wrong containing block.
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(15, 72, 89, 0.32)",
          backdropFilter: "blur(14px) saturate(1.4)",
          WebkitBackdropFilter: "blur(14px) saturate(1.4)",
          zIndex: 9999,
        },
      }}
      contentLabel={title}
    >
      <div className={`bm-card ${widthClass}`}>
        <div className="bm-header">
          <div className="bm-head-left">
            {icon !== undefined && icon !== null && icon !== "" ? (
              <span className="bm-icon" aria-hidden="true">
                {icon}
              </span>
            ) : null}
            {truncateTitle ? (
              <>
                <h3
                  className="bm-title bm-title--truncate"
                  data-tooltip-id="bm-title-tip"
                  data-tooltip-content={title}
                  data-tooltip-place="bottom"
                  data-tooltip-delay-show={300}
                >
                  {title}
                </h3>
                <Tooltip id="bm-title-tip" className="bm-title-tip" positionStrategy="fixed" />
              </>
            ) : (
              <h3 className="bm-title">{title}</h3>
            )}
          </div>
          {hideCloseButton ? null : (
            <button
              type="button"
              className="bm-close"
              onClick={onRequestClose}
              aria-label={i18n.t("close")}
            >
              ✕
            </button>
          )}
        </div>
        <div className="bm-body">{children}</div>
      </div>
    </Modal>
  );
};

export default BaseModal;
